import { PostAddress, formatPostAddress, parsePostAddress } from 'utils/socialCampaign/postEditor'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

/**
 * The post editor's address (v2 principle 3, prototype v3 `openEditor` / `closeEditor`): opening a post pushes
 * `#post=<id>` (or `#post=new&date=…&time=…`), so Back closes the editor and a reload reopens it. Walking to another
 * post replaces the entry; closing goes back when the editor pushed it, else drops the hash in place. Only the hash
 * changes: the property page is keyed on its pathname, nothing remounts.
 */
export function usePostEditorAddress() {
  const location = useLocation()
  const navigate = useNavigate()
  const address = useMemo(() => parsePostAddress(location.hash), [location.hash])
  const { pathname, search } = location
  // whether this page pushed the entry the editor sits on (a reload or a shared link did not)
  const pushed = useRef(false)
  useEffect(() => {
    if (!address) pushed.current = false
  }, [address])

  /** Opens the editor; from an open editor (a post written by hand once scheduled) the entry is replaced */
  const open = useCallback((next: PostAddress) => {
    if (address) {
      navigate({ pathname, search, hash: formatPostAddress(next) }, { replace: true })
      return
    }
    pushed.current = true
    navigate({ pathname, search, hash: formatPostAddress(next) })
  }, [address, navigate, pathname, search])

  const goTo = useCallback((postId: string) => {
    navigate({ pathname, search, hash: formatPostAddress({ kind: 'post', postId }) }, { replace: true })
  }, [navigate, pathname, search])

  const close = useCallback(() => {
    if (pushed.current) {
      pushed.current = false
      navigate(-1)
      return
    }
    navigate({ pathname, search, hash: '' }, { replace: true })
  }, [navigate, pathname, search])

  /** An address naming nothing the page can open (a post gone, a campaign ended): dropped without walking history */
  const dismiss = useCallback(() => {
    navigate({ pathname, search, hash: '' }, { replace: true })
  }, [navigate, pathname, search])

  return { address, open, goTo, close, dismiss }
}
